import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    CardActionArea,
    Divider,
    Typography,
} from "@mui/material"
import React, { useState } from "react"

import { IJob, IJobListing } from "../../entities/job/interface"
import { theme } from "../../styles/theme"
import GroupJobStops from "./GroupJobStops"
import { JobTile } from "./JobTile"
import { SwipeDirections, useSwipeable } from "react-swipeable"

const AddressAndCompany: React.FC<{ address: string; company: string }> = ({ address, company }) => (
    <Box>
        <Typography variant="body2" color="white" sx={{ fontWeight: "medium", fontSize: "1.1rem" }}>
            {address}
        </Typography>
        <Typography variant="body2" color="text.secondary">
            {company}
        </Typography>
    </Box>
)

interface IGroupJobTileProps {
    jobs: IJobListing[][]
    index: string
    handleJobStarted: (job: IJob) => Promise<void>
    swipeThreshold: number
}

const GroupJobTile = ({ jobs, index, handleJobStarted, swipeThreshold }: IGroupJobTileProps) => {
    const [showJobs, setShowJobs] = useState(false)
    const [stopModelOpen, setStopModelOpen] = useState(false)

    const handleClick = () => {
        setShowJobs(!showJobs)
    }

    // The individual whole jobs in the group (not split into pickup and delivery)
    const groupedJobs = jobs
        .flatMap((jobListing) => jobListing)
        .reduce((acc: IJobListing[], job) => {
            if (!acc.some((existingJob) => existingJob._id === job._id)) {
                acc.push(job)
            }
            return acc
        }, [])

    const firstStop = jobs[0][0]
    const lastStop = jobs[jobs.length - 1][jobs[jobs.length - 1].length - 1]
    const originAddress = firstStop.originAddress.address
    const originCompany = firstStop.origin.company
    const destinationAddress = lastStop.destinationAddress.address
    const destinationCompany = lastStop.destination.company

    const [translateX, setTranslateX] = useState(0)
    const [swipeDirection, setSwipeDirection] = useState<SwipeDirections | null>(null)

    const handlers = useSwipeable({
        onSwipeStart: (eventData) => {
            setSwipeDirection(eventData.dir)
        },
        onSwiping: (eventData) => {
            if (swipeDirection === "Right") {
                setTranslateX(eventData.deltaX)
            }
        },
        onSwipedRight: (eventData) => {
            if (eventData.deltaX > swipeThreshold) {
                handleJobStarted(firstStop)
            }
        },
        onSwiped: () => {
            setTranslateX(0)
            setSwipeDirection(null)
        },
        ...{
            delta: 10, // min distance(px) before a swipe starts. *See Notes*
            preventScrollOnSwipe: false, // prevents scroll during swipe (*See Details*)
            trackTouch: true, // track touch input
            trackMouse: true, // track mouse input
            rotationAngle: 0, // set a rotation angle
            swipeDuration: Infinity, // allowable duration of a swipe (ms). *See Notes*
            touchEventOptions: { passive: true }, // options for touch listeners (*See Details*)
        },
    })

    return (
        <Box
            key={index}
            {...handlers}
            sx={{
                width: "100%",
                marginTop: { xs: 3, sm: 4, md: 5 },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                alignItems: "right",
                transition: "transform 0.2s ease-out, background-color 0.5s",
                transform: `translateX(${Math.max(0, translateX)}px)`,
                boxShadow: theme.shadows[5],
                "@keyframes bounce": {
                    "0%": { left: "0px" },
                    "5%": { left: "5px" },
                    "10%": { left: "0px" },
                    "15%": { left: "3px" },
                    "20%": { left: "0" },
                },
                animation: "bounce 5s ease 5s infinite",
            }}
        >
            {/* Counter Avatar */}
            <Box width="100%" position="absolute" top="-20px" right={1} display="flex" justifyContent="end" zIndex={1}>
                <Avatar
                    sx={{
                        boxShadow: 10,
                        backgroundColor: "white",
                        position: "relative",
                    }}
                    onClick={(e) => {
                        e.stopPropagation() // Stop the click event from propagating to the parent Box
                        setStopModelOpen(true) // Open modal
                    }}
                >
                    {groupedJobs.length}
                </Avatar>
            </Box>
            <Accordion
                sx={{
                    background: "none",
                    margin: "0px !important",
                    borderRadius: `${theme.spacing(5)} !important`,
                    "::before": {
                        backgroundColor: "unset",
                    },
                }}
            >
                <Box
                    width="100%"
                    display="flex"
                    flexDirection="row"
                    borderRadius={5}
                    sx={{
                        bgcolor: "secondary.light",
                        boxShadow: theme.shadows[5],
                        borderBottomLeftRadius: showJobs ? 0 : "auto",
                        borderBottomRightRadius: showJobs ? 0 : "auto",
                        transition: "border-radius 0.5s",
                    }}
                >
                    <CardActionArea onClick={handleClick}>
                        <AccordionSummary sx={{ margin: 0 }}>
                            <Box padding={2}>
                                {/* Origin Address and Company */}
                                <AddressAndCompany address={originAddress} company={originCompany} />
                                <Divider
                                    sx={{
                                        width: "20%",
                                        borderStyle: "solid",
                                        marginTop: "6%",
                                        marginBottom: "6%",
                                        borderColor: "white",
                                    }}
                                />
                                {/* Destination Address and Company */}
                                <AddressAndCompany address={destinationAddress} company={destinationCompany} />
                            </Box>
                        </AccordionSummary>
                    </CardActionArea>
                </Box>
                <AccordionDetails sx={{ padding: 0 }}>
                    {groupedJobs.map((job, index: number) => {
                        return (
                            <JobTile
                                key={job._id || index}
                                job={job}
                                index={index.toString()}
                                handleJobStarted={handleJobStarted}
                                isGrouped={true}
                                isLast={index === groupedJobs.length - 1}
                                swipeThreshold={swipeThreshold}
                            />
                        )
                    })}
                </AccordionDetails>
            </Accordion>

            <GroupJobStops isOpen={stopModelOpen} onClose={() => setStopModelOpen(false)} groupJobs={jobs} />
        </Box>
    )
}

export default GroupJobTile
